import React, { useEffect, useState, useRef, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '@/contexts/AuthContext';
import { curriculumDocsAPI } from '@/services/api';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Card } from '@/components/ui/card';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Textarea } from '@/components/ui/textarea';
import { toast } from 'sonner';
import { Home, Upload, Loader2, Trash2, BookOpen, FileText, ClipboardList, Search, Library as LibIcon } from 'lucide-react';

const BOARDS = ['CBSE', 'ICSE', 'IB', 'Cambridge', 'State Board', 'Other'];
const DOC_TYPES = [
  { key: 'book', label: 'Textbook (NCERT etc.)', icon: BookOpen },
  { key: 'syllabus', label: 'Syllabus / Curriculum Guide', icon: ClipboardList },
  { key: 'sample_paper', label: 'Sample / Past Question Paper', icon: FileText },
];
const SESSIONS = ['2025-26', '2026-27', '2027-28', '2028-29', '2029-30'];
const CLASSES = ['3','4','5','6','7','8','9','10','11','12'];

const AdminCurriculum = () => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const isAdmin = user?.role === 'admin';

  const [form, setForm] = useState({
    board: 'CBSE', class_name: '10', subject: 'Mathematics',
    session: '2025-26', doc_type: 'book', book_name: '', title: '', notes: '',
  });
  const [files, setFiles] = useState([]);
  const [uploading, setUploading] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(0);
  const [docs, setDocs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState({ board: '', class_name: '', subject: '', doc_type: '', session: '' });
  const [search, setSearch] = useState('');
  const fileRef = useRef(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const params = {};
      Object.entries(filter).forEach(([k, v]) => { if (v) params[k] = v; });
      const { data } = await curriculumDocsAPI.list(params);
      setDocs(data);
    } catch { toast.error('Failed to load documents'); }
    finally { setLoading(false); }
  }, [filter]);

  useEffect(() => { if (isAdmin) load(); }, [isAdmin, load]);

  if (!isAdmin) {
    return (
      <div className="min-h-screen bg-orange-50 flex items-center justify-center p-6">
        <Card className="p-8 text-center border border-orange-200 max-w-md">
          <h2 className="text-xl font-bold mb-2">Admins only</h2>
          <p className="text-stone-600 mb-4">Only administrators can manage curriculum documents.</p>
          <Button onClick={() => navigate('/dashboard')} className="bg-gradient-to-r from-orange-500 to-amber-500 text-white">Back to Dashboard</Button>
        </Card>
      </div>
    );
  }

  const handleUpload = async (e) => {
    e.preventDefault();
    if (!files.length) { toast.error('Choose at least one PDF or DOCX'); return; }
    const HARD_LIMIT = 500 * 1024 * 1024;
    const oversized = files.find((f) => f.size > HARD_LIMIT);
    if (oversized) { toast.error(`"${oversized.name}" is ${Math.round(oversized.size / (1024 * 1024))} MB — max 500 MB per file`); return; }
    const bad = files.find((f) => !/\.(pdf|docx)$/i.test(f.name));
    if (bad) { toast.error(`"${bad.name}" is not PDF or DOCX`); return; }
    if (form.doc_type === 'book' && !form.book_name.trim()) {
      toast.error('Enter a Book Name (required when Document Type is Book)');
      return;
    }
    setUploading(true);
    setUploadProgress(0);
    const meta = {
      board: form.board, class_name: form.class_name, subject: form.subject,
      session: form.session, doc_type: form.doc_type,
      book_name: form.doc_type === 'book' ? form.book_name : '',
    };

    // Upload files SEQUENTIALLY, one HTTP request per file.
    // This avoids Cloudflare's 100s origin timeout on multi-file bulk requests
    // and gives per-file progress feedback (production has a proxy that
    // returns Cloudflare 520 when the origin takes too long on a fat request).
    let ok = 0;
    const failed = [];
    for (let i = 0; i < files.length; i++) {
      const f = files[i];
      const currentIdx = i + 1;
      setUploadProgress(Math.round(((currentIdx - 1) / files.length) * 100));
      try {
        // For every file (including the first), the chapter name is derived from filename.
        // Only pass user-typed title/notes on the very first file so it doesn't repeat.
        const extra = i === 0 ? { title: form.title, notes: form.notes } : {};
        await curriculumDocsAPI.upload(f, { ...meta, ...extra });
        ok++;
        toast.success(`[${currentIdx}/${files.length}] ${f.name} ✓`, { duration: 2500 });
      } catch (err) {
        const status = err.response?.status;
        const detail = err.response?.data?.detail;
        let msg = 'upload failed';
        if (status === 413) msg = 'file too large for the server proxy';
        else if (status === 403) msg = 'admin only';
        else if (status === 401) msg = 'session expired';
        else if (typeof detail === 'string') msg = detail;
        else if (err.message === 'Network Error') msg = 'network error (possibly Cloudflare 520 — file too complex to parse in time)';
        else if (err.code === 'ECONNABORTED') msg = 'timed out';
        else if (err.message) msg = err.message;
        failed.push({ name: f.name, msg });
        // eslint-disable-next-line no-console
        console.error(`[Curriculum upload] ${f.name} failed`, { status, detail, err });
      }
    }
    setUploadProgress(100);

    if (ok > 0 && failed.length === 0) {
      toast.success(`All ${ok} file${ok > 1 ? 's' : ''} uploaded ✅`);
    } else if (ok > 0) {
      toast.success(`${ok} uploaded, ${failed.length} failed — see individual toasts`, { duration: 6000 });
    }
    failed.slice(0, 5).forEach((f) => toast.error(`${f.name}: ${f.msg}`, { duration: 8000 }));

    setFiles([]);
    if (fileRef.current) fileRef.current.value = '';
    setForm({ ...form, title: '', notes: '' });
    setUploading(false);
    setUploadProgress(0);
    await load();
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Delete this document permanently?')) return;
    try {
      await curriculumDocsAPI.delete(id);
      toast.success('Deleted');
      await load();
    } catch { toast.error('Delete failed'); }
  };

  const filtered = docs.filter((d) => {
    if (!search.trim()) return true;
    const q = search.toLowerCase();
    return [d.title, d.filename, d.subject, d.notes].some((v) => (v || '').toLowerCase().includes(q));
  });

  const totalBy = (type) => docs.filter((d) => d.doc_type === type).length;

  return (
    <div className="min-h-screen bg-gradient-to-br from-orange-50 via-white to-amber-50/30">
      <header className="sticky top-0 z-40 backdrop-blur-xl bg-white/85 border-b border-orange-200/40">
        <div className="max-w-7xl mx-auto px-6 py-4 flex justify-between items-center">
          <Button data-testid="admin-cur-home-btn" variant="ghost" onClick={() => navigate('/dashboard')} className="text-orange-700"><Home className="w-5 h-5 mr-2" /> Home</Button>
          <div className="flex items-center gap-2"><LibIcon className="w-6 h-6 text-orange-600" /><h1 className="text-xl font-bold">Curriculum Library (Admin)</h1></div>
          <div className="w-24" />
        </div>
      </header>

      <main className="max-w-7xl mx-auto px-6 py-8 space-y-8">
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          {DOC_TYPES.map((t) => (
            <Card key={t.key} data-testid={`cur-stat-${t.key}`} className="p-5 border border-orange-100 flex items-center gap-4">
              <div className="w-12 h-12 rounded-xl bg-gradient-to-br from-orange-500 to-amber-500 flex items-center justify-center shadow-md"><t.icon className="w-6 h-6 text-white" /></div>
              <div>
                <p className="text-xs uppercase tracking-widest text-orange-700 font-bold">{t.label}</p>
                <p className="text-2xl font-bold text-[#1C1917]">{totalBy(t.key)}</p>
              </div>
            </Card>
          ))}
        </div>

        <Card className="p-6 border border-orange-200/60 bg-white shadow-sm" data-testid="admin-cur-upload-card">
          <h2 className="text-lg font-bold text-orange-800 mb-4 flex items-center gap-2"><Upload className="w-5 h-5" /> Upload Curriculum Document</h2>
          <form onSubmit={handleUpload} className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <div>
              <Label>Document Type</Label>
              <Select value={form.doc_type} onValueChange={(v) => setForm({ ...form, doc_type: v })}>
                <SelectTrigger data-testid="cur-doctype-select" className="mt-1"><SelectValue /></SelectTrigger>
                <SelectContent>{DOC_TYPES.map((t) => <SelectItem key={t.key} value={t.key}>{t.label}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div>
              <Label>Board</Label>
              <Select value={form.board} onValueChange={(v) => setForm({ ...form, board: v })}>
                <SelectTrigger data-testid="cur-board-select" className="mt-1"><SelectValue /></SelectTrigger>
                <SelectContent>{BOARDS.map((b) => <SelectItem key={b} value={b}>{b}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div>
              <Label>Session</Label>
              <Select value={form.session} onValueChange={(v) => setForm({ ...form, session: v })}>
                <SelectTrigger data-testid="cur-session-select" className="mt-1"><SelectValue /></SelectTrigger>
                <SelectContent>{SESSIONS.map((s) => <SelectItem key={s} value={s}>{s}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div>
              <Label>Class</Label>
              <Select value={form.class_name} onValueChange={(v) => setForm({ ...form, class_name: v })}>
                <SelectTrigger data-testid="cur-class-select" className="mt-1"><SelectValue /></SelectTrigger>
                <SelectContent>{CLASSES.map((c) => <SelectItem key={c} value={c}>Class {c}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div>
              <Label>Subject</Label>
              <Input data-testid="cur-subject-input" value={form.subject} onChange={(e) => setForm({ ...form, subject: e.target.value })} placeholder="Mathematics" className="mt-1" />
            </div>
            {form.doc_type === 'book' && (
              <div data-testid="cur-book-name-field">
                <Label>Book Name (required for Book type)</Label>
                <Input data-testid="cur-book-name-input" value={form.book_name} onChange={(e) => setForm({ ...form, book_name: e.target.value })} placeholder="NCERT Mathematics for Class 10" className="mt-1" />
              </div>
            )}
            <div>
              <Label>Title (optional — auto-set from filename)</Label>
              <Input data-testid="cur-title-input" value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} placeholder="Leave blank to use the filename as chapter name" className="mt-1" />
            </div>
            <div className="md:col-span-3">
              <Label>Notes (optional)</Label>
              <Textarea data-testid="cur-notes-input" value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} placeholder="Any admin remarks about this document..." rows={2} className="mt-1" />
            </div>
            <div className="md:col-span-3">
              <Label>Files ({form.doc_type === 'book' ? 'Book / Chapters' : form.doc_type === 'syllabus' ? 'Syllabus' : 'Sample / Past Year Question Papers'} — PDF / DOCX up to 500 MB each — multiple allowed)</Label>
              <input
                ref={fileRef}
                data-testid="cur-file-input"
                type="file"
                accept=".pdf,.docx"
                multiple
                onChange={(e) => setFiles(Array.from(e.target.files || []))}
                className="mt-2 block w-full text-sm text-stone-700 file:mr-3 file:py-2 file:px-4 file:rounded-md file:border-0 file:text-sm file:font-semibold file:bg-orange-100 file:text-orange-800 hover:file:bg-orange-200"
              />
              {files.length > 0 && (
                <div data-testid="cur-file-list" className="mt-2 space-y-1">
                  <p className="text-xs font-semibold text-emerald-700">
                    {files.length} file{files.length > 1 ? 's' : ''} selected — chapter name will be auto-extracted from each filename:
                  </p>
                  <ul className="text-xs text-stone-700 max-h-40 overflow-auto bg-stone-50 border border-stone-200 rounded p-2 space-y-0.5">
                    {files.map((f, idx) => (
                      <li key={idx} className="flex justify-between gap-2">
                        <span className="truncate">📄 {f.name}</span>
                        <span className="text-stone-500 whitespace-nowrap">{Math.round(f.size / (1024 * 1024) * 10) / 10} MB</span>
                      </li>
                    ))}
                  </ul>
                </div>
              )}
              {uploading && (
                <div className="mt-2">
                  <div className="w-full bg-stone-200 rounded-full h-2 overflow-hidden">
                    <div className="h-2 bg-gradient-to-r from-orange-500 to-amber-500 transition-all" style={{ width: `${uploadProgress}%` }} />
                  </div>
                  <p className="text-xs text-orange-700 mt-1">Uploading one file at a time to avoid proxy timeouts — {uploadProgress}%</p>
                </div>
              )}
            </div>
            <div className="md:col-span-3 flex justify-end">
              <Button data-testid="cur-upload-btn" type="submit" disabled={uploading || files.length === 0} className="bg-gradient-to-r from-orange-500 to-amber-500 hover:from-orange-600 hover:to-amber-600 text-white">
                {uploading ? (<><Loader2 className="w-4 h-4 mr-2 animate-spin" /> {files.length > 1 ? `Uploading ${files.length}…` : 'Extracting…'}</>) : (<><Upload className="w-4 h-4 mr-2" /> Upload {files.length > 1 ? `${files.length} files` : '& Index'}</>)}
              </Button>
            </div>
          </form>
        </Card>

        <Card className="p-6 border border-orange-200/60 bg-white shadow-sm">
          <div className="flex flex-wrap items-center justify-between gap-4 mb-4">
            <h2 className="text-lg font-bold text-orange-800 flex items-center gap-2"><LibIcon className="w-5 h-5" /> Uploaded Documents ({filtered.length})</h2>
            <div className="relative">
              <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-orange-400" />
              <Input data-testid="cur-search-input" value={search} onChange={(e) => setSearch(e.target.value)} placeholder="Search..." className="pl-9 w-64 bg-white/80 border-orange-200" />
            </div>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-5 gap-3 mb-5">
            <Select value={filter.board || 'all'} onValueChange={(v) => setFilter({ ...filter, board: v === 'all' ? '' : v })}>
              <SelectTrigger data-testid="cur-filter-board"><SelectValue placeholder="All boards" /></SelectTrigger>
              <SelectContent><SelectItem value="all">All boards</SelectItem>{BOARDS.map((b) => <SelectItem key={b} value={b}>{b}</SelectItem>)}</SelectContent>
            </Select>
            <Select value={filter.class_name || 'all'} onValueChange={(v) => setFilter({ ...filter, class_name: v === 'all' ? '' : v })}>
              <SelectTrigger data-testid="cur-filter-class"><SelectValue placeholder="All classes" /></SelectTrigger>
              <SelectContent><SelectItem value="all">All classes</SelectItem>{CLASSES.map((c) => <SelectItem key={c} value={c}>Class {c}</SelectItem>)}</SelectContent>
            </Select>
            <Input data-testid="cur-filter-subject" value={filter.subject} onChange={(e) => setFilter({ ...filter, subject: e.target.value })} placeholder="Subject..." />
            <Select value={filter.doc_type || 'all'} onValueChange={(v) => setFilter({ ...filter, doc_type: v === 'all' ? '' : v })}>
              <SelectTrigger data-testid="cur-filter-type"><SelectValue placeholder="All types" /></SelectTrigger>
              <SelectContent><SelectItem value="all">All types</SelectItem>{DOC_TYPES.map((t) => <SelectItem key={t.key} value={t.key}>{t.label}</SelectItem>)}</SelectContent>
            </Select>
            <Select value={filter.session || 'all'} onValueChange={(v) => setFilter({ ...filter, session: v === 'all' ? '' : v })}>
              <SelectTrigger data-testid="cur-filter-session"><SelectValue placeholder="All sessions" /></SelectTrigger>
              <SelectContent><SelectItem value="all">All sessions</SelectItem>{SESSIONS.map((s) => <SelectItem key={s} value={s}>{s}</SelectItem>)}</SelectContent>
            </Select>
          </div>

          {loading ? (
            <div className="text-center py-16"><Loader2 className="w-8 h-8 animate-spin text-orange-500 mx-auto" /></div>
          ) : filtered.length === 0 ? (
            <p className="text-center py-8 text-stone-500">No documents match your filters.</p>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-sm" data-testid="cur-docs-table">
                <thead className="bg-orange-50 border-b border-orange-200">
                  <tr>
                    <th className="text-left px-3 py-2 font-bold text-orange-800">Chapter / Title</th>
                    <th className="text-left px-3 py-2 font-bold text-orange-800">Type</th>
                    <th className="text-left px-3 py-2 font-bold text-orange-800">Board</th>
                    <th className="text-left px-3 py-2 font-bold text-orange-800">Class</th>
                    <th className="text-left px-3 py-2 font-bold text-orange-800">Subject</th>
                    <th className="text-left px-3 py-2 font-bold text-orange-800">Session</th>
                    <th className="text-left px-3 py-2 font-bold text-orange-800">Size</th>
                    <th className="text-right px-3 py-2"></th>
                  </tr>
                </thead>
                <tbody>
                  {filtered.map((d) => (
                    <tr key={d.curriculum_doc_id} data-testid={`cur-doc-row-${d.curriculum_doc_id}`} className="border-b border-stone-100 hover:bg-orange-50/50">
                      <td className="px-3 py-2">
                        <div className="font-semibold">{d.chapter || d.title || d.filename}</div>
                        {d.book_name && <div className="text-xs text-orange-700">📘 {d.book_name}</div>}
                        {d.filename && (d.filename !== d.title) && <div className="text-xs text-stone-500 truncate max-w-xs">📄 {d.filename}</div>}
                      </td>
                      <td className="px-3 py-2"><span className="text-xs uppercase bg-orange-100 text-orange-800 px-2 py-0.5 rounded">{d.doc_type}</span></td>
                      <td className="px-3 py-2">{d.board}</td>
                      <td className="px-3 py-2">{d.class_name}</td>
                      <td className="px-3 py-2">{d.subject}</td>
                      <td className="px-3 py-2">{d.session}</td>
                      <td className="px-3 py-2 text-stone-500">{Math.round((d.text_length || 0) / 1000)}K chars</td>
                      <td className="px-3 py-2 text-right">
                        <Button data-testid={`cur-delete-${d.curriculum_doc_id}`} size="sm" variant="ghost" onClick={() => handleDelete(d.curriculum_doc_id)} className="text-red-600 hover:bg-red-50">
                          <Trash2 className="w-4 h-4" />
                        </Button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </Card>
      </main>
    </div>
  );
};

export default AdminCurriculum;
